import React from "react";

import { Item } from "@radix-ui/react-toggle-group";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { useOverflowDetection } from "@replo/design-system/hooks/use-overflow-detection";
import twMerge from "@replo/design-system/utils/twMerge";

type ToggleProps = {
  size: "base" | "sm";
  tooltipContent?: React.ReactNode;
  value: string;
  className?: string;
  isActive: boolean;
  disabled?: boolean;
  label: React.ReactNode;
} & Omit<React.HTMLAttributes<HTMLButtonElement>, "className">;

const DeprecatedToggle: React.FC<ToggleProps> = ({
  size,
  value,
  isActive,
  tooltipContent,
  className,
  label,
  disabled,
  ...rest
}) => {
  const [itemRef, isOverflowing] = useOverflowDetection();

  const hasTooltip = tooltipContent || isOverflowing;

  const tooltipContentToRender =
    tooltipContent && !isOverflowing ? (
      tooltipContent
    ) : (
      <div className="flex flex-col">
        <div className="text-xs font-medium">{label}</div>
        {tooltipContent && <div className="text-xs">{tooltipContent}</div>}
      </div>
    );

  const ariaLabel = (() => {
    if (typeof tooltipContent === "string") {
      return tooltipContent;
    } else if (typeof label === "string") {
      return label;
    } else {
      return value ?? undefined;
    }
  })();

  const itemElement = (
    <Item
      {...rest}
      value={value}
      className={twMerge(
        "text-muted flex h-full w-full cursor-pointer items-center justify-center rounded-[4px] px-1.5 py-1",
        size === "sm" && "text-xs",
        size === "base" && "text-sm",
        isActive && "text-primary rounded-[4px]",
        disabled && "cursor-not-allowed opacity-50",
        className,
      )}
      aria-label={ariaLabel}
    >
      <span className="cursor-pointer truncate" ref={itemRef}>
        {label}
      </span>
    </Item>
  );

  if (hasTooltip) {
    return (
      <Tooltip content={tooltipContentToRender} triggerAsChild>
        {itemElement}
      </Tooltip>
    );
  }

  return itemElement;
};

export { DeprecatedToggle };
